import {noop} from '@benchling/js-utils';
import {Flex, Box, Text, UnstyledButton, useMantineTheme} from '@mantine/core';
import classNames from 'classnames';
import React, {useEffect, useMemo, useState} from 'react';
import ReactDOM from 'react-dom';

import {DocumentNoteProvider} from 'notebook/documents/DocumentNoteProvider';
import FormulaEditable from 'notebook/tables/core-ui/FormulaEditable';
import FunctionDropdown from 'notebook/tables/core-ui/FunctionDropdown';
import useMultiWindowEditing from 'notebook/tables/core-ui/useMultiWindowEditing';
import ErrorTrianglePopover from 'notebook/tables/errors/ErrorTrianglePopover';
import excess from 'notebook/tables/excess';
import {functions} from 'notebook/tables/excess/functions';
import * as SpreadsheetEvalUtil from 'notebook/tables/spreadsheets/SpreadsheetEvalUtil';
import SpreadsheetStore from 'notebook/tables/spreadsheets/SpreadsheetStore';
import {HydratedTableProvider} from 'notebook/tables/store/contexts/HydratedTableContext';
import {useCrossStepFormulaBox} from 'notebook/tables/store/hooks/useCrossStepFormulaBox';
import {RowMetadata, TableIndex, Cell} from 'notebook/tables/TableConstants';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface CrossStepFormulaBoxProps {
  stepName: string;
  stepIdBeingEdited: string;
  stepIdBeingDisplayed: string;
  tableName: string;
  index: TableIndex;
  tableNamespace: string;
  onReturnToStep: () => void;
}

function CrossStepFormulaBoxInner({
  stepName,
  stepIdBeingEdited,
  stepIdBeingDisplayed,
  tableName,
  index,
  tableNamespace,
  onReturnToStep,
}: CrossStepFormulaBoxProps): JSX.Element {
  const {cid, rowId, cellError, lastIndex, updateCell, onFunctionClick} = useCrossStepFormulaBox(index);
  const formulaBoxRef = React.useRef<HTMLDivElement>(null);

  const [isFunctionDropdownOpen, setIsFunctionDropdownOpen] = useState(false);

  useMultiWindowEditing({
    tableNamespace,
    fileId: stepIdBeingEdited,
    tableName,
    domNode: ReactDOM.findDOMNode(formulaBoxRef.current) as Element,
    multiStartIndex: null,
    multiEndIndex: null,
    hasFormulasDisabled: false,
    multiActive: false,
    multiEditing: true,
    multiCrossEditing: true,
    onEditFinalize: () => {
      const {formula, typedValue} = SpreadsheetStore.getState(tableNamespace, tableName);
      updateCell({text: formula, typedValue});
      finishEditing();
    },
  });

  function finishEditing(): void {
    SpreadsheetEvalUtil.setMultiEditingForTable(tableName, tableNamespace, stepIdBeingEdited, {
      index: null,
      editing: false,
      text: '',
      typedValue: null,
      selection: null,
    });
  }

  function onCellEnter(e: {text: string; cellData: Partial<Cell>}): void {
    updateCell({...e.cellData, text: e.text});
    finishEditing();
    onReturnToStep();
  }

  const formattedError = cellError && (
    <ErrorTrianglePopover
      key="error-popover"
      labelClass="label-danger"
      error={cellError}
      placement={FloatingPlacement.BOTTOM}
    >
      <span className="u-valign-middle u-tiny-spacing-left text-danger">
        <BenchlingIcon icon="warning" />
      </span>
    </ErrorTrianglePopover>
  );

  const rowMetadata: RowMetadata = useMemo(
    () => ({
      rowId,
    }),
    [rowId]
  );

  useEffect(() => {
    if (formulaBoxRef.current && stepIdBeingDisplayed) {
      formulaBoxRef.current.focus();
    }
  }, [formulaBoxRef, stepIdBeingDisplayed]);

  const theme = useMantineTheme();

  return (
    <Box
      sx={{
        borderTopWidth: '1px',
        borderTopStyle: 'solid',
        borderTopColor: theme.colors.gray[2],
        padding: '0 !important', // Important because we have to override table-floater's padding
        boxShadow: '4px 0px 4px rgba(0, 0, 0, 0.2)',
        zIndex: 1,
      }}
      className="table-floater js-mediocre-active-edit"
      ref={formulaBoxRef}
    >
      <Flex
        direction="row"
        w="100%"
        p={`${theme.spacing.xs} ${theme.spacing.lg}`}
        sx={{
          borderBottom: `1px solid ${theme.colors.gray[2]}`,
        }}
      >
        <UnstyledButton
          onClick={() => {
            onReturnToStep();
          }}
        >
          <Flex direction="row" align="center">
            <Text>
              {stepName}
              &nbsp;|&nbsp;
              {tableName}
            </Text>
            <BenchlingIcon icon="arrow-up" style={{marginLeft: theme.spacing.sm}} />
          </Flex>
        </UnstyledButton>
        <UnstyledButton
          onClick={() => {
            finishEditing();
          }}
          sx={{marginLeft: 'auto'}}
        >
          <BenchlingIcon icon="close" />
        </UnstyledButton>
      </Flex>
      <Box
        sx={{
          padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
        }}
        className="formula-box-wrapper input-group"
      >
        <div className="input-group-btn" key="formula-group">
          <FunctionDropdown
            additionalFunctions={[]}
            className="_functionDropdownBtn"
            isOpen={isFunctionDropdownOpen}
            primaryFunctions={functions}
            setDropdownOpenState={(isOpen: boolean) => {
              setIsFunctionDropdownOpen(isOpen);
            }}
            onFunctionClick={onFunctionClick}
            disabled={false}
          />
        </div>
        <div className="current-cell-label input-group-addon" key="formula-cell-label">
          <span className="label label-primary">
            {excess.util.colNumberToLetters(index[1])}
            {excess.util.rowIndexToNumber(index[0])}
          </span>
          {formattedError}
        </div>
        <FormulaEditable
          key="cross-step-formula-box"
          fileId={stepIdBeingEdited}
          tableName={tableName}
          tableNamespace={tableNamespace}
          isFormulaBox={true}
          className={classNames('formula-box form-control multi-editable u-cursor-text')}
          onFormulaFinalize={onCellEnter}
          onFormulaFocus={noop}
          escapeHandler={() => {
            finishEditing();
          }}
          index={index}
          cid={cid}
          locked={false}
          rowMetadata={rowMetadata}
          getLastIndex={() => lastIndex ?? index}
          disableFormulas={false}
        />
      </Box>
    </Box>
  );
}

export function CrossStepFormulaBox(props: CrossStepFormulaBoxProps): JSX.Element {
  return (
    <DocumentNoteProvider documentId={props.stepIdBeingEdited} tableNamespace={props.tableNamespace}>
      <HydratedTableProvider tableName={props.tableName}>
        <CrossStepFormulaBoxInner {...props} />
      </HydratedTableProvider>
    </DocumentNoteProvider>
  );
}
